import React, { useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { selectRoute, Route } from "src/config/routes";
import { Role } from "src/network/User";
import { UI } from "@wwimmo/ui";
import { GeneralCommunicationSettings } from "./general/GeneralCommunicationSettings";
import { KrediFlowCommunicationSettings } from "./krediflow/KrediFlowCommunicationSettings";
import { EasyContactTicketingCommunicationSettings } from "./easycontactTicketing/EasyContactTicketingCommunicationSettings";
import styles from "./CommunicationSettings.module.css";

enum CommunicationSettingsTab {
    GENERAL = "general",
    KREDIFLOW = "krediflow",
    EASYCONTACT_TICKETING = "easycontact-ticketing"
}

const getInitialTab = (search: string, allowedTabIds: string[]): string => {
    const params = new URLSearchParams(search);
    const tab = params.get("tab");
    if (tab && allowedTabIds.includes(tab)) {
        return tab;
    }
    return CommunicationSettingsTab.GENERAL;
};

const CommunicationSettingsBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { navStore, uiSidebarStore, communicationSettingsStore, authStore } = useContext(RootStoreContext);

    const isAdministrator = authStore.user?.availableRoles?.includes(Role.ADMINISTRATOR) ?? false;
    const isEcAdmin = authStore.user?.availableRoles?.includes(Role.ECADMIN) ?? false;

    const showKrediFlowTab = isAdministrator && navStore.hasFeatureKrediFlow;
    const showEasyContactTicketingTab =
        ((isEcAdmin || isAdministrator) && navStore.hasFeatureEasyContact) ||
        (isAdministrator && navStore.hasFeatureTicketing);

    const tabContents = [
        { id: CommunicationSettingsTab.GENERAL, label: t("screens.communication_settings.tabs.general") },
        ...(showKrediFlowTab
            ? [{ id: CommunicationSettingsTab.KREDIFLOW, label: t("screens.communication_settings.tabs.krediflow") }]
            : []),
        ...(showEasyContactTicketingTab
            ? [
                  {
                      id: CommunicationSettingsTab.EASYCONTACT_TICKETING,
                      label: t("screens.communication_settings.tabs.easycontact_ticketing")
                  }
              ]
            : [])
    ];

    const allowedTabIds = tabContents.map((tab) => tab.id);
    const [activeTab, setActiveTab] = useState<string>(() => getInitialTab(props.location.search, allowedTabIds));
    const effectiveActiveTab = allowedTabIds.includes(activeTab) ? activeTab : CommunicationSettingsTab.GENERAL;

    useEffect(() => {
        communicationSettingsStore.loadCommunicationSettings();
    }, [communicationSettingsStore]);

    useEffect(() => {
        if (showKrediFlowTab) {
            communicationSettingsStore.loadKrediFlowSettings();
        }
    }, [communicationSettingsStore, showKrediFlowTab]);

    useEffect(() => {
        if (showEasyContactTicketingTab) {
            communicationSettingsStore.loadEasyContactTicketingSettings();
        }
    }, [communicationSettingsStore, showEasyContactTicketingTab]);

    useEffect(() => {
        const params = new URLSearchParams(props.location.search);
        const urlTab = params.get("tab");
        if (urlTab && allowedTabIds.includes(urlTab)) {
            setActiveTab(urlTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showKrediFlowTab, showEasyContactTicketingTab, props.location.search]);

    useEffect(() => {
        navStore.setTitle(t("screens.communication_settings.title"));
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);

        if (uiSidebarStore.isSidebarActive) {
            navStore.setBackbutton(null);
        } else {
            navStore.setBackbutton(selectRoute(Route.profile), t("screens.account.title").toString());
        }
    }, [navStore, t, uiSidebarStore.isSidebarActive]);

    const onTabChange = (tabId: string) => {
        setActiveTab(tabId);
        const params = new URLSearchParams(props.location.search);
        params.set("tab", tabId);
        props.history.replace({ search: params.toString() });
    };

    return (
        <React.Fragment>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col xl={9}>
                        <UI.Title>{t("screens.communication_settings.title")}</UI.Title>
                    </UI.Col>
                </UI.Row>
                {tabContents.length > 1 && (
                    <div className={styles.Tabbar}>
                        <UI.Tabs
                            key={tabContents.length}
                            selectedId={effectiveActiveTab}
                            onTabChange={onTabChange}
                            contents={tabContents}
                            nowrap
                        />
                    </div>
                )}
                {effectiveActiveTab === CommunicationSettingsTab.GENERAL && <GeneralCommunicationSettings />}
                {effectiveActiveTab === CommunicationSettingsTab.KREDIFLOW && <KrediFlowCommunicationSettings />}
                {effectiveActiveTab === CommunicationSettingsTab.EASYCONTACT_TICKETING && (
                    <EasyContactTicketingCommunicationSettings />
                )}
            </UI.Container>
        </React.Fragment>
    );
};

export const CommunicationSettings = withRouter(Sentry.withProfiler(observer(CommunicationSettingsBase)));
